import type {
    PatchWorksheetSubmissionResultItem,
    PostAnswerAssetResultAsset,
    Retailer,
    RetailerWorksheetSubmission,
} from '@packages/clients';

import type {
    AnswerAssetEntity,
    WorksheetSubmissionEntity,
    WorksheetSubmissionGroupEntity,
    WorksheetSubmissionTitleEntity,
} from '../entities';

/**
 * Map a {@link PostAnswerAssetResultAsset} DTO to an {@link AnswerAssetEntity}.
 *
 * @param dto - the asset metadata from the create answer assets response
 * @returns the domain entity
 * @example
 * ```ts
 * const entities = response.data.results.assets.map((item) => toAnswerAssetEntity(item.asset));
 * ```
 */
export function toAnswerAssetEntity(dto: PostAnswerAssetResultAsset): AnswerAssetEntity {
    return {
        assetDispositionId: dto.assetDispositionId,
        assetDispositionIdDescription: dto.assetDispositionIdDescription,
        assetId: dto.assetId,
        assetProcessingStateId: dto.assetProcessingStateId,
        assetProcessingStateIdDescription: dto.assetProcessingStateIdDescription,
        assetTypeId: dto.assetTypeId,
        assetTypeIdDescription: dto.assetTypeIdDescription,
        createdTimestamp: dto.createdTimestamp,
        createdTimestampDescription: dto.createdTimestampDescription,
        isImportedFromSiebel: dto.importedFromSiebel,
        name: dto.name,
        percentUploaded: dto.percentUploaded,
        percentUploadedDescription: dto.percentUploadedDescription,
    };
}

/**
 * Map a {@link PatchWorksheetSubmissionResultItem} DTO (patch response) to a
 * {@link WorksheetSubmissionEntity}.
 *
 * @param dto - the result item from the patch submission response
 * @returns the domain entity
 * @example
 * ```ts
 * const entity = toWorksheetSubmissionEntityFromPatchResult(response.data.results);
 * ```
 */
export function toWorksheetSubmissionEntityFromPatchResult(
    dto: PatchWorksheetSubmissionResultItem
): WorksheetSubmissionEntity {
    return {
        caseNumber: dto.caseNumber,
        lastUpdatedTimestamp: dto.lastUpdatedTimestamp,
        lastUpdatedTimestampForDisplay: dto.lastUpdatedTimestampForDisplay,
        retailerId: dto.retailerId,
        status: dto.status,
        statusCode: dto.statusCode,
        submissionId: dto.submissionId,
        userName: dto.userName,
        worksheetId: dto.worksheetId,
    };
}

/**
 * Map a {@link WorksheetSubmissions} DTO (list-level summary) to a {@link WorksheetSubmissionEntity}.
 *
 * @param dto - the worksheet submission summary from case details
 * @returns the domain entity
 * @example
 * ```ts
 * const entity = toWorksheetSubmissionEntityFromSummary(submissionDto);
 * ```
 */
export function toWorksheetSubmissionEntityFromSummary(dto: RetailerWorksheetSubmission): WorksheetSubmissionEntity {
    return {
        lastUpdatedTimestamp: dto.lastUpdatedTimestamp,
        lastUpdatedTimestampForDisplay: dto.lastUpdatedTimestampForDisplay,
        retailerNumber: dto.retailerNumber,
        status: dto.status,
        statusCode: dto.statusCode,
        submissionId: dto.submissionId,
        userDisplayName: dto.userDisplayName,
        userName: dto.userName,
        worksheetCaseType: dto.worksheetCaseType,
        worksheetCaseTypeSourceId: dto.worksheetCaseTypeSourceId,
        worksheetId: dto.worksheetId,
        worksheetTitle: dto.worksheetTitle,
    };
}

/**
 * Map a single entry from `Retailer.WorksheetSubmissionGroups` to a
 * {@link WorksheetSubmissionGroupEntity}.
 *
 * @param dto - the submission group object from case details
 * @returns the domain entity
 * @example
 * ```ts
 * const entities = groups?.map(toWorksheetSubmissionGroupEntity) ?? [];
 * ```
 */
export function toWorksheetSubmissionGroupEntity(
    dto: NonNullable<Retailer['WorksheetSubmissionGroups']>[number]
): WorksheetSubmissionGroupEntity {
    return {
        groupDescription: dto.groupDescription,
        groupName: dto.groupName,
        worksheetCaseGroupId: dto.worksheetCaseGroupId,
        worksheetSubmissionTitles: dto.worksheetSubmissionTitles.map(toWorksheetSubmissionTitleEntity),
    };
}

/**
 * Map a submission title from `Retailer.WorksheetSubmissionGroups` to a
 * {@link WorksheetSubmissionTitleEntity}.
 *
 * @param dto - the submission title object
 * @returns the domain entity
 */
function toWorksheetSubmissionTitleEntity(
    dto: NonNullable<Retailer['WorksheetSubmissionGroups']>[number]['worksheetSubmissionTitles'][number]
): WorksheetSubmissionTitleEntity {
    return {
        lastUpdatedTimestamp: dto.lastUpdatedTimestamp,
        status: dto.status,
        submissionId: dto.submissionId,
        userDisplayName: dto.userDisplayName,
        worksheetDescription: dto.worksheetDescription,
        worksheetId: dto.worksheetId,
        worksheetTitle: dto.worksheetTitle,
    };
}
